import type { IOutputNoun } from '@featit/shared/flows/step-taken';
import { workAreaGql } from '@/data-sources/WorkAreaApiRegistry';

/**
 * Reads and writes for a work item's admission holds (F-0300 WN-8, T-0391 GV-2d):
 * `docs/strategy/product-model-flows-and-streams.md` §7.1 GV-2/GV-2a/GV-2d. A hold is a filing
 * waiting for a different actor's confirmation, or an agent's move waiting for a person's — one
 * table, told apart by `kind`.
 */

/**
 * `filing` and `move` are the admission holds WN-8 already covers; `confirmation`, `time` and
 * `event` are a workflow node's own request, opened when any path enters a node whose `waitsFor`
 * names one (F-0300 WN-14). A read asking no `kinds` sees only `filing` and `move`, as before WN-14.
 */
export type WorkItemHoldKindType = 'filing' | 'move' | 'confirmation' | 'time' | 'event';

/** Every kind a holds read may ask for (WN-14p) — pass this to see node requests too. */
export const ALL_HOLD_KINDS: WorkItemHoldKindType[] = [
  'filing',
  'move',
  'confirmation',
  'time',
  'event',
];

/** How many of a node's siblings' requests took each output (F-0300 WN-14p, "3 of 7 confirmed"). */
export interface IWorkItemRequestTallyOutput {
  output: string;
  count: number;
}

/** A node's requests among the items that `addresses` the same fixed item as this one (WN-14p). */
export interface IWorkItemRequestTally {
  total: number;
  outputs: IWorkItemRequestTallyOutput[];
}

/** A pending hold, as `workAreaHolds` / `workItemHold` answer it. */
export interface IWorkItemHold {
  id: string;
  kind: WorkItemHoldKindType;
  /** The work item's id — what `confirmWorkItemAdmission`/`confirmWorkItemMove`/`declineWorkItemMove`/`repositionWorkItem` take, not this hold's own id. */
  workItemId: string;
  itemKey: string;
  itemTitle: string;
  itemTypeKey: string;
  currentStatusKey: string | null;
  /** The names the work area gives those keys: what the item detail shows (T-0391). */
  currentStatusName: string | null;
  requestedStatusKey: string;
  requestedStatusName: string;
  /** The flow node a move asks for, and its name in the flow in force; absent on a filing. */
  requestedNode: string | null;
  requestedNodeName: string | null;
  requestedOutput: string | null;
  reason: string;
  filedByActorType: string | null;
  filedByDisplayName: string | null;
  filedAt: string;
  autoCancelAt: string | null;
  /**
   * A node's request: the outputs an answer may take (WN-14p). Null for a filing or a move, which
   * answer through `confirmHold`/`declineMove`, not `answerWorkItemRequest`.
   */
  answerOutputs: string[] | null;
  /** The catalogue keys of those outputs' words, where the flow names them (AGM-27, T-0801). */
  answerOutputLabels?: Array<{
    output: string;
    labelKey: string;
    tallyNoun?: IOutputNoun | null;
  }> | null;
  /** A node's request of an item that `addresses` another: its siblings' tally. Null otherwise. */
  tally: IWorkItemRequestTally | null;
  /**
   * The same `confirmationCheck` the mutations enforce, run for the reader as a person (GV-2a): no
   * surface offers a button the server would refuse.
   */
  viewerMayAnswer: boolean;
  viewerMayNotAnswerBecause: string | null;
  /**
   * The same reason as a code a screen words itself (F-0621 design review): `approval-others` when an
   * approval waits on other people. Absent from a server that predates it; the English reason stands.
   */
  viewerMayNotAnswerCode?: string | null;
  /**
   * For an approval node (F-0300 WN-44): every approver and their answer so far, and the tally.
   * `kind` and `role` say who an approver is as codes (`responsible`, `reporter`, `user`, `role`).
   */
  approvers?: Array<{
    name: string;
    answer: string | null;
    kind?: string | null;
    role?: string | null;
  }> | null;
  approvalTally?: string | null;
}

/** One node of a work area's flow, as `workAreaFlow` answers it (F-0300 WN-9). */
export interface IWorkAreaFlowNode {
  key: string;
  name: string;
  preset: string;
  outputs: string[];
  entryActions: Array<{ kind: string; status?: string }>;
  actors?: Record<string, unknown>;
}

export interface IWorkAreaFlow {
  workAreaId: string;
  itemTypeKey: string;
  origin: 'generated' | 'authored';
  version: number;
  definition: {
    nodes: IWorkAreaFlowNode[];
    connections: Array<{ from: string; output: string; to: string }>;
    variables: unknown[];
  };
}

/** What every read of a hold selects: the Holds reads here, and a Tasks for me hold row (T-0492). */
export const HOLD_FIELDS = `
  id
  kind
  workItemId
  itemKey
  itemTitle
  itemTypeKey
  currentStatusKey
  currentStatusName
  requestedStatusKey
  requestedStatusName
  requestedNode
  requestedNodeName
  requestedOutput
  reason
  filedByActorType
  filedByDisplayName
  filedAt
  autoCancelAt
  answerOutputs
  answerOutputLabels { output labelKey tallyNoun { en { one other } de { one other } } }
  tally {
    total
    outputs {
      output
      count
    }
  }
  viewerMayAnswer
  viewerMayNotAnswerBecause
  approvers {
    name
    answer
    kind
    role
  }
  approvalTally
  viewerMayNotAnswerCode
`;

/**
 * A work area's pending holds, oldest first (GV-2a). `kinds` defaults to `filing` and `move`
 * (server-side, WN-14p); pass `ALL_HOLD_KINDS` to see a workflow node's requests too.
 */
export function fetchWorkAreaHolds(
  workAreaId: string,
  kinds?: WorkItemHoldKindType[],
): Promise<IWorkItemHold[]> {
  return workAreaGql(workAreaId)<{ workAreaHolds: IWorkItemHold[] }>(
    `query WorkAreaHolds($workAreaId: ID!, $kinds: [String!]) {
      workAreaHolds(workAreaId: $workAreaId, kinds: $kinds) { ${HOLD_FIELDS} }
    }`,
    { workAreaId, kinds: kinds ?? null },
  ).then((res) => res.workAreaHolds);
}

/** This item's pending hold of the kinds asked for (`filing`/`move` by default), or `null` (GV-2a, WN-14p). */
export function fetchWorkItemHold(
  itemId: string,
  kinds?: WorkItemHoldKindType[],
  /** The item's work area: its hold is read from the deployment that holds it (I-1120). */
  workAreaId?: string | null,
): Promise<IWorkItemHold | null> {
  return workAreaGql(workAreaId)<{ workItemHold: IWorkItemHold | null }>(
    `query WorkItemHold($id: ID!, $kinds: [String!]) {
      workItemHold(id: $id, kinds: $kinds) { ${HOLD_FIELDS} }
    }`,
    { id: itemId, kinds: kinds ?? null },
  ).then((res) => res.workItemHold);
}

function confirmFiling(workItemId: string, workAreaId?: string | null): Promise<{ id: string }> {
  return workAreaGql(workAreaId)<{ confirmWorkItemAdmission: { id: string } }>(
    `mutation ConfirmWorkItemAdmission($id: ID!) {
      confirmWorkItemAdmission(id: $id) { id }
    }`,
    { id: workItemId },
  ).then((res) => res.confirmWorkItemAdmission);
}

function confirmMove(workItemId: string, workAreaId?: string | null): Promise<{ id: string }> {
  return workAreaGql(workAreaId)<{ confirmWorkItemMove: { id: string } }>(
    `mutation ConfirmWorkItemMove($id: ID!) {
      confirmWorkItemMove(id: $id) { id }
    }`,
    { id: workItemId },
  ).then((res) => res.confirmWorkItemMove);
}

/**
 * Confirm whichever kind this hold is (GV-2d) — the branch the mutation test pins: sending
 * `confirmWorkItemAdmission` for a move leaves the move held, so the branch has to be here, read
 * from the hold's own `kind`, never assumed by a caller.
 */
export function confirmHold(
  hold: Pick<IWorkItemHold, 'kind' | 'workItemId'> & { workAreaId?: string | null },
): Promise<{ id: string }> {
  return hold.kind === 'filing'
    ? confirmFiling(hold.workItemId, hold.workAreaId)
    : confirmMove(hold.workItemId, hold.workAreaId);
}

/** A person declines a held move, with an optional note (GV-2d). A filing has no decline. */
export function declineMove(
  workItemId: string,
  note?: string | null,
  workAreaId?: string | null,
): Promise<{ id: string }> {
  return workAreaGql(workAreaId)<{ declineWorkItemMove: { id: string } }>(
    `mutation DeclineWorkItemMove($id: ID!, $note: String) {
      declineWorkItemMove(id: $id, note: $note) { id }
    }`,
    { id: workItemId, note: note ?? null },
  ).then((res) => res.declineWorkItemMove);
}

/**
 * A person answers a workflow node's request by taking one of its outputs (F-0300 WN-14/WN-14p):
 * `answerOutputs` on the hold names which. Refused for an agent, and for a `time`/`event` wait —
 * those answer themselves (a sweep, or the matching event) — the same boundary GV-2 draws for a
 * held move.
 */
export function answerWorkItemRequest(
  workItemId: string,
  output: string,
  note?: string | null,
  /** The node whose request this answers: an item on parallel paths may wait on several (WN-16). */
  node?: string | null,
  workAreaId?: string | null,
): Promise<{ id: string }> {
  return workAreaGql(workAreaId)<{ answerWorkItemRequest: { id: string } }>(
    `mutation AnswerWorkItemRequest($id: ID!, $output: String!, $note: String, $node: String) {
      answerWorkItemRequest(id: $id, output: $output, note: $note, node: $node) { id }
    }`,
    { id: workItemId, output, note: note ?? null, node: node ?? null },
  ).then((res) => res.answerWorkItemRequest);
}

/** A work-area admin places an item at a node (or nodes) of its flow, with a required reason. */
export function repositionWorkItem(
  workItemId: string,
  nodes: string[],
  reason: string,
  workAreaId?: string | null,
): Promise<{ id: string }> {
  return workAreaGql(workAreaId)<{ repositionWorkItem: { id: string } }>(
    `mutation RepositionWorkItem($id: ID!, $nodes: [String!]!, $reason: String!) {
      repositionWorkItem(id: $id, nodes: $nodes, reason: $reason) { id }
    }`,
    { id: workItemId, nodes, reason },
  ).then((res) => res.repositionWorkItem);
}

/** The item type's flow in this work area, for the Reposition dialog's choice of node(s). */
export function fetchWorkAreaFlow(workAreaId: string, itemTypeKey: string): Promise<IWorkAreaFlow> {
  return workAreaGql(workAreaId)<{ workAreaFlow: IWorkAreaFlow }>(
    `query WorkAreaFlow($workAreaId: ID!, $itemTypeKey: String!) {
      workAreaFlow(workAreaId: $workAreaId, itemTypeKey: $itemTypeKey)
    }`,
    { workAreaId, itemTypeKey },
  ).then((res) => res.workAreaFlow);
}
